import { Helmet } from "react-helmet";

import { getAllPostsV2, getPostBySlugV2 } from "lib/posts";
import { formatDate } from "lib/datetime";
import { ArticleJsonLd } from "lib/json-ld";
import { getSiteData, helmetSettingsFromMetadata } from "lib/site";
import useSite, { SiteContext, useSiteContext } from "hooks/use-site";
import usePageMetadata from "hooks/use-page-metadata";

import Layout from "components/Layout";
import Header from "components/Header";
import Section from "components/Section";
import Container from "components/Container";
import Content from "components/Content";
import Metadata from "components/Metadata";
import FeaturedImage from "components/FeaturedImage";
import Loading from "components/Loading";

import styles from "styles/pages/Post.module.scss";
import { adjustSlugLast } from "../../../plugins/util";
import { abort } from "lib/abort";
import Error from "components/Error";
import { getShowUi } from "data/fetchPlugin";
import ReactPlayer from "react-player";

export default function Post(props) {
  if (props.hasError) {
    return <Error statusCode={props.code} />;
  }
  if (props.post) {
    const {
      post,
      socialImage,
      showUi,
      locale,
      siteData,
      relativePath
    } = props;
    const {
      title,
      metaTitle,
      description,
      content,
      date,
      author,
      categories,
      modified,
      featuredImage,
      isSticky = false,
    } = post;

    const { metadata: siteMetadata = {}, homepage } = useSite();
    const lang = locale.toLowerCase();

    if (!post.og) {
      post.og = {};
    }

    post.og.imageUrl = `${homepage}${socialImage}`;
    post.og.imageSecureUrl = post.og.imageUrl;
    post.og.imageWidth = 2000;
    post.og.imageHeight = 1000;

    const { metadata } = usePageMetadata({
      metadata: {
        ...post,
        title: metaTitle,
        description:
          description || post.og?.description || `Read more about ${title}`,
      },
    });

    if (process.env.WORDPRESS_PLUGIN_SEO !== true) {
      metadata.title = `${title} - ${siteMetadata.title}`;
      metadata.og.title = metadata.title;
      metadata.twitter.title = metadata.title;
    }

    const metadataOptions = {
      compactCategories: false,
    };

    const helmetSettings = helmetSettingsFromMetadata(metadata);

    const site = useSiteContext({
      showUi,
      locale,
      siteData,
      relativePath
    });

    return (
      <SiteContext.Provider value={site}>
        <Layout isFullColor={true}>
          <div className={styles.topPost}>
            <Helmet {...helmetSettings} />

            <ArticleJsonLd post={post} siteTitle={siteMetadata.title} />

            <Header
              style={{
                marginTop: "5rem",
                marginBottom: 0,
              }}>
              {featuredImage && (
                <FeaturedImage
                  {...featuredImage}
                  src={featuredImage.sourceUrl}
                  dangerouslySetInnerHTML={featuredImage.caption}
                />
              )}
              <h1
                className={styles.title}
                dangerouslySetInnerHTML={{
                  __html: title,
                }}
              />
              {post?.videoNews?.videoUrl && (
                <div className={styles.videoContainer}>
                  <div className={styles.videoPreview}>
                    <ReactPlayer
                      className={styles.reactPlayer}
                      url={post?.videoNews?.videoUrl}
                      controls={true}
                      width="100%"
                      height="100%"
                    />
                  </div>
                </div>
              )}
              <Metadata
                className={styles.postMetadata}
                date={date}
                author={author}
                categories={categories}
                options={metadataOptions}
                isSticky={isSticky}
              />
            </Header>

            <Content>
              <Section>
                <Container>
                  <div
                    className={styles.content}
                    dangerouslySetInnerHTML={{
                      __html: content,
                    }}
                  />
                </Container>
              </Section>
            </Content>

            <Section className={styles.postFooter}>
              <Container>
                <p className={styles.postModified}>
                  {lang === "th" ? "อัพเดทล่าสุด" : "Last updated on"}{" "}
                  {formatDate(modified, locale)}.
                </p>
              </Container>
            </Section>
          </div>
        </Layout>
      </SiteContext.Provider>
    );
  } else {
    return <Loading />;
  }
}

export async function getStaticProps({ params = {}, locale } = {}) {
  const currentSlug = adjustSlugLast(params?.slug);
  const { post } = await getPostBySlugV2(currentSlug, locale);

  if (post?.code === "not_found" && post?.data?.status === 404) {
    return abort(404);
  }
  const socialImage = `${process.env.OG_IMAGE_DIRECTORY}/${currentSlug}.png`;
  const siteData = await getSiteData();
  const showUi = await getShowUi();

  return {
    props: {
      post,
      socialImage,
      showUi,
      locale,
      siteData,
      relativePath: post.relative_path
    },
  };
}

export async function getStaticPaths({ locales }) {
  const paths = [];
  for (const locale of locales) {
    const { posts } = await getAllPostsV2(locale);
    posts
      .filter(({ slug }) => typeof slug === "string")
      .map(({ slug }) => {
        paths.push({
          params: {
            slug: [`${slug}`],
          },
          locale,
        });
      });
  }
  return {
    paths,
    fallback: "blocking",
  };
}
